import React, { useState, useEffect, useCallback, useRef, Suspense } from 'react';
import { View, ScrollView, Pressable, Modal } from 'react-native';
import { Link, Slot, usePathname } from 'expo-router';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import Logo from '@/components/Icons/Logo';
import ThemeSelector from '@/components/Navigation/ThemeSelector';
import { useLanding } from '@/components/Landing/LandingKit';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import DocsSearchButton from '@/components/Docs/DocsSearchButton';
import { DocsTopicLink } from '@/components/Docs/DocsDesign';
import { DocsTOC } from '@/components/Docs/DocsTOC';
import { DocsFeedback } from '@/components/Docs/DocsFeedback';
import { docsNavigation, getDocHref } from '@/constants/docsNavigation';
import useGuideNavigation from '@/hooks/useGuideNavigation';
import { LearnProgressProvider } from '@/components/Learn/LearnProgress';
import { getLearnStructure } from '@/constants/learnCurriculum';

const LazyDocsSearchModal = React.lazy(() => import('@/components/Docs/DocsSearch').then(m => ({ default: m.DocsSearchModal })));

function SidebarSection({ section, pathname, closeMenu, expanded, toggle, guideType }) {
  const { c } = useLanding();
  return (
    <View style={{ marginTop: 24 }}>
      <Pressable onPress={toggle} accessibilityRole="button" accessibilityLabel={`${section.title} section`} aria-expanded={expanded} style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', minHeight: 40, paddingHorizontal: 12, gap: 12 }}>
        <Text weight="semibold" style={{ color: c.ink, fontSize: 13, lineHeight: 20 }}>{section.title}</Text>
        <Icon name={expanded ? 'chevron-down' : 'chevron-right'} size={9} color={c.muted} />
      </Pressable>
      {expanded && section.items.map(item => {
        const href = guideType === 'learn' ? (item.href || (item.slug === 'index' ? '/docs#learn' : `/docs/learn/${item.slug}`)) : item.slug === 'index' ? '/docs/index' : item.href || getDocHref(item.slug);
        return <DocsTopicLink key={item.slug} compact title={item.label} href={href} active={pathname === href} onPress={closeMenu} />;
      })}
    </View>
  );
}

const learnNavigation = getLearnStructure();

export default function GuideLayout() {
  const pathname = usePathname();
  const isLearn = pathname.startsWith('/docs/learn/');
  const hub = '/docs';
  const label = 'Guides';
  const SearchModal = LazyDocsSearchModal;
  const { c, band, isXS, isCompact, width } = useLanding();
  const insets = useSafeAreaInsets();
  const isHome = pathname === hub || pathname === `${hub}/`;
  const { sections: docsSections } = useGuideNavigation('docs', { staticFallback: docsNavigation });
  const { sections: learnSections } = useGuideNavigation('learn', { staticFallback: learnNavigation });
  const navigation = [...docsSections.map(section => ({ ...section, guideType: 'docs' })), ...learnSections.map(section => ({ ...section, title: `Learn · ${section.title}`, guideType: 'learn' }))];
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [expanded, setExpanded] = useState({});
  const contentRef = useRef(null);
  const sidebarRef = useRef(null);
  const menuRef = useRef(null);
  const closeMenu = useCallback(() => setMobileOpen(false), []);
  const openSearch = useCallback(() => setSearchOpen(true), []);
  const showTOC = !isHome && !isLearn && !pathname.startsWith('/docs/work') && width > 1340;

  useEffect(() => {
    contentRef.current?.scrollTo({ y: 0, animated: false });
    setMobileOpen(false);
  }, [pathname]);

  useEffect(() => {
    let frame;
    const scrollToSection = () => {
      const id = window.location.hash.slice(1);
      if (!['learn', 'documentation', 'learn-lessons'].includes(id)) return;
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => document.getElementById(id)?.scrollIntoView({ block: 'start' }));
    };
    scrollToSection();
    window.addEventListener('hashchange', scrollToSection);
    return () => { cancelAnimationFrame(frame); window.removeEventListener('hashchange', scrollToSection); };
  }, [pathname]);

  useEffect(() => {
    const node = sidebarRef.current?.getScrollableNode?.();
    node?.querySelector('[aria-current="page"]')?.scrollIntoView({ block: 'nearest' });
  }, [pathname, mobileOpen]);

  const sidebar = (
    <ScrollView ref={sidebarRef} showsVerticalScrollIndicator={false} contentContainerStyle={{ padding: 16, paddingBottom: 40 }}>
      <DocsTopicLink compact title="Guides" href={hub} active={isHome} onPress={closeMenu} />
      {navigation.map(section => <SidebarSection guideType={section.guideType} key={section.title} section={section} pathname={pathname} closeMenu={closeMenu} expanded={expanded[section.title] ?? true} toggle={() => setExpanded(prev => ({ ...prev, [section.title]: !(prev[section.title] ?? true) }))} />)}
      <View style={{ marginTop: 28, paddingTop: 16, borderTopWidth: 1, borderTopColor: c.rule }}><DocsTopicLink compact title="Back to Gab AI" href="/" onPress={closeMenu} /></View>
    </ScrollView>
  );

  return (
    <LearnProgressProvider><View style={{ flex: 1, backgroundColor: c.page, ...(!isLearn && { colorScheme: 'light' }) }}>
      <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 12, minHeight: (isXS ? 64 : 72) + insets.top, paddingTop: insets.top, paddingHorizontal: isXS ? 16 : 28, borderBottomWidth: 1, borderBottomColor: c.rule, backgroundColor: c.page }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: isXS ? 12 : 20, minWidth: 0 }}>
          {isCompact && <Pressable ref={menuRef} onPress={() => setMobileOpen(true)} accessibilityRole="button" accessibilityLabel="Open guides menu" aria-expanded={mobileOpen} style={{ minWidth: 40, minHeight: 40, alignItems: 'center', justifyContent: 'center' }}><Icon name="bars" size={17} color={c.ink} /></Pressable>}
          <Link href="/" asChild><Pressable accessibilityLabel="Gab AI home" style={{ flexDirection: 'row', alignItems: 'center', gap: 8, minHeight: 40 }}><Logo width={20} height={20} color={c.ink} />{!isXS && <Text weight="semibold" style={{ color: c.ink, fontSize: 17 }}>Gab AI</Text>}</Pressable></Link>
          <Link href={hub} asChild><Pressable style={{ minHeight: 40, justifyContent: 'center' }}><Text style={{ color: c.inkSoft, fontSize: 15 }}>{label}</Text></Pressable></Link>
        </View>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: isXS ? 10 : 18 }}>
          <DocsSearchButton onPress={openSearch} label="Search guides and lessons" placeholder="Search guides..." />
          {isLearn && !isCompact && <ThemeSelector showAccentSelector={false} />}
          <Link href="/signup" asChild><Pressable style={{ paddingHorizontal: isXS ? 12 : 16, minHeight: 38, justifyContent: 'center', borderRadius: 6, backgroundColor: c.accentFill }}><Text weight="semibold" style={{ color: c.onAccent, fontSize: 13 }}>{isXS ? 'Try free' : 'Get started'}</Text></Pressable></Link>
        </View>
      </View>
      <View style={{ flex: 1, flexDirection: 'row', minHeight: 0 }}>
        {!isCompact && !(isLearn && isHome) && <View accessibilityRole="navigation" accessibilityLabel="Guide navigation" style={{ width: 252, borderRightWidth: 1, borderRightColor: c.rule, backgroundColor: band.bg }}>{sidebar}</View>}
        <ScrollView ref={contentRef} style={{ flex: 1, minWidth: 0 }} showsVerticalScrollIndicator={false}>
          <View style={{ width: '100%', maxWidth: showTOC ? 1040 : isHome ? (isLearn ? 1280 : 1100) : 860, alignSelf: 'center', flexDirection: 'row', alignItems: 'flex-start' }}>
            <View accessibilityRole="main" nativeID="main-content" style={{ flex: 1, minWidth: 0, paddingHorizontal: isXS ? 20 : 48, paddingTop: isXS ? 32 : 48, paddingBottom: 72 }}>
              <View key={pathname} dataSet={{ docsContent: 'true' }}><Slot /></View>
              {!isHome && !isLearn && <DocsFeedback />}
            </View>
            {showTOC && <DocsTOC accentColor={c.accent} />}
          </View>
        </ScrollView>
      </View>
      <Modal visible={mobileOpen && isCompact} transparent animationType="fade" onRequestClose={closeMenu} onDismiss={() => menuRef.current?.focus?.()}>
        <Pressable onPress={closeMenu} accessibilityLabel="Dismiss guides menu" style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.4)' }}>
          <Pressable onPress={event => event.stopPropagation()} accessibilityRole="navigation" accessibilityLabel="Guide navigation" style={{ width: '88%', maxWidth: 320, height: '100%', backgroundColor: c.page, paddingTop: insets.top }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 20, minHeight: 64, borderBottomWidth: 1, borderBottomColor: c.rule }}>
              <Text weight="semibold" style={{ color: c.ink, fontSize: 16 }}>Guides</Text>
              <Pressable onPress={closeMenu} accessibilityRole="button" accessibilityLabel="Close guides menu" style={{ minWidth: 40, minHeight: 40, alignItems: 'center', justifyContent: 'center' }}><Icon name="xmark" size={17} color={c.ink} /></Pressable>
            </View>
            {sidebar}
          </Pressable>
        </Pressable>
      </Modal>
      {searchOpen && <Suspense fallback={null}><SearchModal isOpen onClose={() => setSearchOpen(false)} /></Suspense>}
    </View></LearnProgressProvider>
  );
}
